// src/modules/goldVault/pages/BranchVaultDashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Statistic, Table, Tag, Button, Typography, Spin,
  Select, Space,
} from 'antd';
import { ReloadOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { getBranchVault, getBranchLedger } from '../api';
import { fetchBranches, fetchBranchById } from '../../branches/api';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;
const fmtW = (v) => `${Number(v || 0).toFixed(3)}g`;
const INV_TX_COLORS = {
  PURCHASE: 'green', PLEDGE_RELEASE: 'blue', TRANSFER_TO_HO: 'red',
  ADJUSTMENT: 'orange', RETURN: 'cyan', AUDIT_ADJUSTMENT: 'purple',
};

export default function BranchVaultDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { role } = usePermissions();
  const isAdmin = role === 'admin';

  const [isHO, setIsHO] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [vault, setVault] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerMeta, setLedgerMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Resolve branch context: HO/admin gets a dropdown (all branches), branch users load their own
  useEffect(() => {
    const init = async () => {
      try {
        if (isAdmin) {
          setIsHO(true);
          const res = await fetchBranches({ limit: 200 });
          const list = Array.isArray(res) ? res : (res.data || []);
          setBranches(list);
          if (list.length > 0) setSelectedBranch(list[0]._id);
        } else if (user?.branch) {
          const branchRes = await fetchBranchById(user.branch);
          const branch = branchRes?.data || branchRes;
          if (branch?.branchType === 'HO') {
            setIsHO(true);
            const res = await fetchBranches({ limit: 200 });
            const list = Array.isArray(res) ? res : (res.data || []);
            setBranches(list);
            if (list.length > 0) setSelectedBranch(list[0]._id);
          } else {
            // Regular branch user — always load their own branch directly
            const branchId = typeof user.branch === 'object' ? user.branch._id : user.branch;
            setSelectedBranch(branchId);
          }
        }
      } catch (e) {
        console.error('BranchVaultDashboard init:', e);
      }
    };
    init();
  }, [isAdmin, user?.branch]);

  useEffect(() => {
    if (selectedBranch) {
      loadVault();
      loadLedger();
    }
  }, [selectedBranch]);

  const loadVault = async () => {
    if (!selectedBranch) return;
    setLoading(true);
    try {
      const res = await getBranchVault(selectedBranch);
      setVault(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadLedger = async (page = 1) => {
    if (!selectedBranch) return;
    setLedgerLoading(true);
    try {
      const res = await getBranchLedger(selectedBranch, { page, limit: 10 });
      setLedger(res.data || []);
      setLedgerMeta(res.meta || {});
    } catch (e) { console.error(e); }
    finally { setLedgerLoading(false); }
  };

  const ledgerColumns = [
    { title: 'Ref No.', dataIndex: 'referenceNo', key: 'referenceNo', width: 140 },
    { title: 'Date', dataIndex: 'businessDate', key: 'businessDate', width: 110,
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
    {
      title: 'Type', dataIndex: 'transactionType', key: 'transactionType', width: 140,
      render: (v) => <Tag color={INV_TX_COLORS[v] || 'default'}>{v}</Tag>,
    },
    { title: 'Case', dataIndex: 'caseNumber', key: 'caseNumber', width: 110 },
    { title: 'Gross In',  dataIndex: 'grossWeightIn',      key: 'gi', width: 90,  render: (v) => v > 0 ? <Text type="success">{fmtW(v)}</Text> : '—' },
    { title: 'Gross Out', dataIndex: 'grossWeightOut',     key: 'go', width: 90,  render: (v) => v > 0 ? <Text type="danger">{fmtW(v)}</Text>   : '—' },
    { title: 'Running Gross', dataIndex: 'runningGrossWeight', key: 'rg', width: 110, render: fmtW },
    { title: 'Running Net',   dataIndex: 'runningNetWeight',   key: 'rn', width: 110, render: fmtW },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Branch Vault</Title></Col>
        <Col>
          <Space>
            {isHO && (
              <Select
                placeholder="Select Branch"
                style={{ width: 200 }}
                value={selectedBranch}
                onChange={setSelectedBranch}
              >
                {branches.map((b) => (
                  <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>
                ))}
              </Select>
            )}
            <Button icon={<ReloadOutlined />} onClick={() => { loadVault(); loadLedger(); }}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {[
            { title: 'Current Gross Weight', value: fmtW(vault?.currentGrossWeight) },
            { title: 'Current Net Weight',   value: fmtW(vault?.currentNetWeight)   },
            { title: 'Total Items',          value: vault?.totalItems || 0           },
            { title: 'Vault Status',         value: vault?.status || 'ACTIVE'        },
          ].map((s) => (
            <Col xs={12} sm={6} key={s.title}>
              <Card size="small" bordered>
                <Statistic title={s.title} value={s.value} valueStyle={{ fontSize: 18, fontWeight: 600 }} />
              </Card>
            </Col>
          ))}
        </Row>
      </Spin>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Button type="primary" icon={<ArrowRightOutlined />} onClick={() => navigate('/vault/transfers/create')}>
            Create Transfer to HO
          </Button>
        </Col>
        <Col>
          <Button onClick={() => navigate('/vault/reconciliation')}>
            Reconciliation
          </Button>
        </Col>
        <Col>
          <Button onClick={() => navigate('/vault/aging')}>
            Aging Report
          </Button>
        </Col>
      </Row>

      <Card title="Gold Vault Ledger" size="small">
        <Spin spinning={ledgerLoading}>
          <Table
            dataSource={ledger}
            rowKey="_id"
            columns={ledgerColumns}
            scroll={{ x: 800 }}
            size="small"
            pagination={{
              current: ledgerMeta.page,
              total: ledgerMeta.total,
              pageSize: 10,
              onChange: (page) => loadLedger(page),
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
